import { styled } from '@mui/system';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import { mergeRight, uniq } from 'ramda';
import * as React from 'react';
import MedicationUnit from 'Routes/Medication/utils/medicationUnit';
import { sortStrengths } from 'Utility/randomUtil';
import UnitSelect from '../components/UnitSelect';
import { StrengthSelect } from './../components/StrengthSelect';

import type { IRegimenContext } from '..';

const StyledUnit = styled('span')({
  margin: '0.5rem',
});

// Generate strengths array for current medication from available packages
const strengthsArray = (packages: IMedicationPackage[]): { [key: string]: string }[] => {
  const strengths = packages.map((p: IMedicationPackage) => p.strengths);
  return uniq(strengths).sort((a1, a2) => sortStrengths(a1, a2));
};

const strengthsOnChange =
  (
    onChange: IFormData['onChange'],
    strArr: IStrengths[],
    thisRegimen: IRegimenOnDemand,
    allRegimen: IRegimenOnDemand[],
    index: number,
  ) =>
  (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = [strArr[values[name] as number]];

    const thisRegimens = { ...thisRegimen };
    const allRegimens = [...allRegimen];

    allRegimens[index] = mergeRight(thisRegimens, { [name]: value as any, vnr: '' });
    onChange?.({ regimen: allRegimens });
  };

// OnChange for other fields
const otherOnChange =
  (onChange: IFormData['onChange'], thisRegimen: IRegimenOnDemand, allRegimen: IRegimenOnDemand[], index: number) =>
  (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    const thisRegimens = { ...thisRegimen };
    const allRegimens = [...allRegimen];

    allRegimens[index] = mergeRight(thisRegimens, { [name]: value } as any);
    // Update all regimen
    onChange?.({ regimen: allRegimens });
  };

const OnDemand = ({ formData, editIndex, packages }: IRegimenContext): React.JSX.Element => {
  const regimen = formData.document.regimen?.[editIndex] as IRegimenOnDemand;
  const strengths = React.useMemo(() => regimen.strengths ?? [], [regimen.strengths]);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const availableStrengths = packages ? strengthsArray(packages) : [];

  // Use this for most onChange needs. Complicated due to the need of updating regimen array
  const generalOnChange = otherOnChange(
    formData.onChange,
    regimen,
    formData.document.regimen as IRegimenOnDemand[],
    editIndex,
  );

  return (
    <React.Fragment>
      <StrengthSelect
        formData={formData}
        availableStrengths={availableStrengths}
        strengthsOnChange={strengthsOnChange(
          formData.onChange,
          availableStrengths,
          regimen,
          formData.document.regimen as IRegimenOnDemand[],
          editIndex,
        )}
        strengths={strengths}
      />
      <UnitSelect unitValue={regimen.unit} onChange={generalOnChange} />

      <FormRow title="medication.doseSize" headerWidth={3}>
        <InputHandler
          type="TextField"
          name="dose"
          editing={true}
          formData={{
            onChange: generalOnChange,
            document: { dose: regimen.dose },
          }}
          placeholder="medication.doseSize"
        />

        <StyledUnit>
          <MedicationUnit unit={regimen.unit} />
        </StyledUnit>
      </FormRow>
      <FormRow title="medication.maxDoses" headerWidth={3}>
        <InputHandler
          type="NumberField"
          name="maxDoses"
          editing={true}
          formData={{
            onChange: generalOnChange,
            document: { maxDoses: regimen.maxDoses },
          }}
          placeholder="medication.maxDoses"
          precision={2}
        />
        <StyledUnit>
          <MedicationUnit unit={regimen.unit} />
        </StyledUnit>
      </FormRow>
    </React.Fragment>
  );
};

interface IStrengths {
  [key: string]: string;
}

export default OnDemand;
